/**
 * Document Permission Management Hooks
 * Reuses the V3 users API (same as QMS permissions) with document-specific permission keys.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSession } from '@/hooks/useSession';
import { api } from '@/lib/api';

// --- Types ---

export interface UserWithPermissions {
  _id: string;
  email: string;
  firstname: string;
  lastname: string;
  permission_strings: string[];
}

// --- Permission keys ---

export const DOC_PERMISSIONS = [
  {
    key: 'documents.access',
    label: 'Access Documents',
    description: 'Can see the Documents section in the sidebar and access all documents',
  },
] as const;

export type DocPermissionKey = (typeof DOC_PERMISSIONS)[number]['key'];

// --- Query keys ---

const docPermissionKeys = {
  all: ['doc-permissions'] as const,
  users: (search?: string) => [...docPermissionKeys.all, 'users', search] as const,
};

// --- Helpers ---

export function hasDocPermission(permissionStrings: string[], key: DocPermissionKey): boolean {
  return permissionStrings.includes(key);
}

export function toggleDocPermission(permissionStrings: string[], key: DocPermissionKey, enabled: boolean): string[] {
  if (enabled) {
    return permissionStrings.includes(key) ? permissionStrings : [...permissionStrings, key];
  }
  return permissionStrings.filter((p) => p !== key);
}

// --- API — V4 (PostgreSQL) only ---

const EXCLUDED_EMAILS = [
  'a.sicimli@lechiffre.ca',
  'claudecode@atek.io',
  'omar@test.io',
  'appscripts@atek.io',
  'cindyatek@yopmail.com',
];

async function fetchUsers(search?: string): Promise<UserWithPermissions[]> {
  const params: Record<string, string | number> = {
    employment_type: 'employee,contractor',
    enabled: 'true',
    is_subaccount: 'false',
    pageLength: 100,
  };
  if (search) params.search = search;

  const response = await api.get<{
    users: Array<{
      pg_id: string;
      email: string;
      firstname?: string;
      lastname?: string;
      permission_strings?: string[];
    }>;
    total: number;
  }>('/v4/users', params);

  return (response.users || [])
    .filter((u) => !EXCLUDED_EMAILS.includes(u.email.toLowerCase()))
    .map((u) => ({
      _id: u.pg_id,
      email: u.email,
      firstname: u.firstname || '',
      lastname: u.lastname || '',
      permission_strings: u.permission_strings || [],
    }));
}

async function updatePermissions(userId: string, permissionStrings: string[]): Promise<void> {
  await api.put(`/v4/users/${userId}/permission_strings`, {
    permission_strings: permissionStrings,
  });
}

// --- Hooks ---

export function useDocumentUsers(search?: string) {
  return useQuery({
    queryKey: docPermissionKeys.users(search),
    queryFn: () => fetchUsers(search),
    staleTime: 30_000,
  });
}

export function useUpdateDocPermissions() {
  const queryClient = useQueryClient();
  const { user: currentUser, invalidateSession } = useSession();

  return useMutation({
    mutationFn: ({ userId, permissionStrings }: { userId: string; permissionStrings: string[]; permissionKey: string }) =>
      updatePermissions(userId, permissionStrings),
    onMutate: async ({ userId, permissionStrings }) => {
      await queryClient.cancelQueries({ queryKey: docPermissionKeys.all });
      const previous = queryClient.getQueriesData<UserWithPermissions[]>({ queryKey: docPermissionKeys.all });
      queryClient.setQueriesData<UserWithPermissions[]>(
        { queryKey: docPermissionKeys.all },
        (old) => old?.map((u) => u._id === userId ? { ...u, permission_strings: permissionStrings } : u),
      );
      return { previous };
    },
    onSuccess: (_data, variables) => {
      toast.success('Permission updated');
      if (currentUser?._id === variables.userId) {
        invalidateSession();
      }
    },
    onError: (_err, _vars, context) => {
      toast.error('Failed to update permission');
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: docPermissionKeys.all });
    },
  });
}
